import React, { useMemo, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Plus, RotateCcw, Search, Check } from 'lucide-react';
import {
  WIDGET_REGISTRY, DEFAULT_LAYOUT, CATEGORY_ORDER, CATEGORY_META,
  widgetIsAllowed, resolveLayout, categoryColor,
} from './registry';

// iOS Widget Gallery–style picker: centered modal, big widget cards in a
// responsive grid, horizontal category chips at the top, search box.
// Replaces the right-side drawer that was here before — feels more like
// a deliberate "pick something" surface and less like a side panel.
export default function WidgetPicker({ open, onClose, layout, onChange, authCtx }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      const id = setTimeout(() => inputRef.current.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const inLayout = useMemo(() => new Map(layout.map(l => [l.id, l])), [layout]);

  // Categories present in the allowed set (so we don't show empty chips)
  const allowed = useMemo(
    () => WIDGET_REGISTRY.filter(w => widgetIsAllowed(w, authCtx)),
    [authCtx]
  );
  const cats = useMemo(() => {
    const present = new Set(allowed.map(w => w.category || 'other'));
    return ['all', ...CATEGORY_ORDER.filter(c => present.has(c))];
  }, [allowed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allowed.filter(w => {
      if (activeCat !== 'all' && (w.category || 'other') !== activeCat) return false;
      if (!q) return true;
      const title = t(w.titleKey).toLowerCase();
      const desc = w.descriptionKey ? t(w.descriptionKey).toLowerCase() : '';
      return title.includes(q) || desc.includes(q) || w.id.includes(q);
    });
  }, [allowed, activeCat, search, t]);

  const totalVisible = layout.filter(l => !l.hidden).length;

  if (!open) return null;

  const addWidget = (id) => {
    const widget = WIDGET_REGISTRY.find(w => w.id === id);
    if (!widget) return;
    const existing = inLayout.get(id);
    if (existing) onChange(layout.map(l => (l.id === id ? { ...l, hidden: false } : l)));
    else onChange([...layout, { id, size: widget.defaultSize || 'md', hidden: false }]);
  };
  const removeWidget = (id) => onChange(layout.map(l => (l.id === id ? { ...l, hidden: true } : l)));
  const resetDefaults = () => {
    if (!window.confirm(t('dashboard.widgets.confirmReset', 'Reset dashboard to default layout?'))) return;
    onChange(resolveLayout(DEFAULT_LAYOUT, authCtx));
  };

  const modal = (
    <div
      onMouseDown={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 920, maxHeight: '88vh',
          background: 'var(--c-bg)',
          border: '1px solid var(--c-border)',
          borderRadius: 18,
          boxShadow: 'var(--shadow-lg), 0 30px 80px rgba(0,0,0,0.35)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px 12px',
          borderBottom: '1px solid var(--c-border-subtle)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>
                {t('dashboard.widgets.gallery', 'Widget gallery')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 3 }}>
                {totalVisible} {t('dashboard.widgets.ofTotal', 'of')} {allowed.length} {t('dashboard.widgets.visible', 'visible')} · {t('dashboard.widgets.galleryHint', 'tap a widget to add or remove')}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 32, height: 32, borderRadius: '50%',
                border: 'none', background: 'var(--c-surface-2)',
                color: 'var(--c-text)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
            >
              <X size={16} />
            </button>
          </div>

          {/* Search */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, color: 'var(--c-text-muted)', pointerEvents: 'none' }} />
            <input
              ref={inputRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('dashboard.widgets.searchPlaceholder', 'Search widgets…')}
              className="form-input"
              style={{
                paddingLeft: 36, paddingRight: 32,
                fontSize: 14, height: 38, borderRadius: 999,
                background: 'var(--c-surface-2)',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute', right: 6, padding: 6,
                  border: 'none', background: 'transparent',
                  color: 'var(--c-text-muted)', cursor: 'pointer',
                  borderRadius: '50%',
                }}
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Category chips (horizontal scroll on overflow) */}
          <div style={{
            display: 'flex', gap: 6, overflowX: 'auto',
            paddingBottom: 2, marginLeft: -4, marginRight: -4, paddingLeft: 4, paddingRight: 4,
          }}>
            {cats.map(c => {
              const meta = c === 'all'
                ? { labelKey: 'dashboard.widgets.allCategories', emoji: '✨' }
                : (CATEGORY_META[c] || CATEGORY_META.other);
              const isActive = activeCat === c;
              const chipColor = c === 'all' ? 'var(--c-brand)' : categoryColor(c);
              return (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 12px', height: 30, borderRadius: 999,
                    border: '1px solid ' + (isActive ? chipColor : 'var(--c-border)'),
                    background: isActive ? chipColor : 'var(--c-surface)',
                    color: isActive ? 'white' : 'var(--c-text)',
                    fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', flexShrink: 0,
                    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                  }}
                >
                  <span style={{ fontSize: 13 }}>{meta.emoji}</span>
                  {c === 'all'
                    ? t('dashboard.widgets.allCategories', 'All')
                    : t(meta.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Widget grid */}
        <div style={{
          flex: 1, overflowY: 'auto',
          padding: 18,
        }}>
          {filtered.length === 0 ? (
            <div style={{
              padding: '60px 20px', textAlign: 'center',
              color: 'var(--c-text-muted)', fontSize: 13,
            }}>
              {t('dashboard.widgets.noResults', 'No widgets match your search.')}
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
            }}>
              {filtered.map(widget => {
                const entry = inLayout.get(widget.id);
                const isVisible = entry && !entry.hidden;
                return (
                  <WidgetCard
                    key={widget.id}
                    widget={widget}
                    isVisible={isVisible}
                    onAdd={() => addWidget(widget.id)}
                    onRemove={() => removeWidget(widget.id)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--c-border-subtle)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          background: 'var(--c-surface-2)',
        }}>
          <button
            onClick={resetDefaults}
            style={{
              padding: '7px 12px', fontSize: 12, fontWeight: 500,
              background: 'transparent', border: 'none',
              borderRadius: 8, color: 'var(--c-text-muted)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <RotateCcw size={12} /> {t('dashboard.widgets.reset', 'Reset to default')}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '8px 22px', fontSize: 13, fontWeight: 600,
              background: 'var(--c-brand)', border: 'none',
              borderRadius: 999, color: 'white',
              cursor: 'pointer',
            }}
          >
            {t('common.done', 'Done')}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

function WidgetCard({ widget, isVisible, onAdd, onRemove }) {
  const { t } = useTranslation();
  const Icon = widget.icon;
  const cat = widget.category || 'other';
  const catMeta = CATEGORY_META[cat] || CATEGORY_META.other;
  const color = categoryColor(cat);

  return (
    <button
      onClick={isVisible ? onRemove : onAdd}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        gap: 10,
        padding: 14,
        background: isVisible ? 'rgba(34, 197, 94, 0.06)' : 'var(--c-surface)',
        border: '1px solid ' + (isVisible ? 'rgba(34, 197, 94, 0.4)' : 'var(--c-border)'),
        borderRadius: 16,
        cursor: 'pointer', textAlign: 'left',
        color: 'var(--c-text)',
        position: 'relative',
        transition: 'transform 0.1s, border-color 0.15s, background 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
        if (!isVisible) e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        if (!isVisible) e.currentTarget.style.borderColor = 'var(--c-border)';
      }}
    >
      {/* Top row: iOS-style app-icon squircle + check/plus badge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        {Icon && (
          <div style={{
            width: 50, height: 50, borderRadius: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(145deg, ${color}, ${color}cc)`,
            color: '#fff',
            boxShadow: `0 4px 10px ${color}40, inset 0 1px 0 rgba(255,255,255,0.25)`,
          }}>
            <Icon size={24} strokeWidth={2.2} />
          </div>
        )}
        <div style={{
          width: 26, height: 26, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: isVisible ? '#22c55e' : 'var(--c-surface-2)',
          color: isVisible ? 'white' : 'var(--c-text-muted)',
          flexShrink: 0,
          boxShadow: isVisible ? '0 2px 4px rgba(34, 197, 94, 0.3)' : 'none',
          transition: 'background 0.15s',
        }}>
          {isVisible ? <Check size={14} strokeWidth={3} /> : <Plus size={14} strokeWidth={2.5} />}
        </div>
      </div>

      {/* Title + desc */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3, lineHeight: 1.2 }}>
          {t(widget.titleKey)}
        </div>
        {widget.descriptionKey && (
          <div style={{
            fontSize: 11, color: 'var(--c-text-muted)',
            lineHeight: 1.4, minHeight: '2em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {t(widget.descriptionKey)}
          </div>
        )}
      </div>

      {/* Category footer */}
      <div style={{
        fontSize: 10, color: 'var(--c-text-muted)',
        display: 'flex', alignItems: 'center', gap: 4,
        textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
      }}>
        <span style={{ fontSize: 11 }}>{catMeta.emoji}</span>
        {t(catMeta.labelKey)}
      </div>
    </button>
  );
}
